import {useParams, Link} from "wouter";
import React, {useEffect, useMemo, useState} from "react";
import styles from "./page.module.css";
import DateRangeButtons from "@/components/DateRangeSelector/DateRangeButtons";
import MetricLineChart from "@/components/Charts/MetricLineChart";
import MetricStackedBarChart from "@/components/Charts/MetricStackedBarChart";
import RadialPercentageChart from "@/components/Charts/RadialPercentageChart";
import AreaChart from "@/components/Charts/AreaChart";
import ProductsSoldTable from "./ProductsSold/ProductsSoldTable";
import LoadingSpinner from "@/components/LoadingSpinner/LoadingSpinner";
import { formatValue } from "@/utils/formatUtils";
import PageContainer from "@/app/settings/container/PageContainer";
import Skeleton from "@/components/Skeleton/Skeleton";
import { DateRangeValue, startOfToday, endOfToday } from "@/utils/dateUtils";
import { getLocalStorageItem } from "@/utils/localStorageUtils";
import { useCache } from "@/hooks/cacheHooks/useCache";
import { computeCacheKey } from "../../profit-by-sku/page";
import { ProfitabilityByCategoryBreakdown } from "@boxem/core-api-schema/lib/seller-analytics";
import { getCategoryProfitabilityBreakDownByCategory } from "@/api/seller-analytics";
import { BuyCostTile } from "@/components/BuyCostBox/BuyCostTile";
import { useErrorModalContext } from "@/contexts/ErrorModalContext";

const ProfitByCategoryPage = () => {
    const { handleErrorResponseGenerically } = useErrorModalContext();
    const metrics = [
        { key: 'revenue', label: 'Revenue', color: '#DF3600' },
        { key: 'cogs', label: 'Buy Cost', color: '#5823FF' },
        { key: 'fees', label: 'Fees', color: '#F3D047' },
        { key: 'profit', label: 'Profit', color: '#A7DCB4' }
    ];

    const params = useParams<{ category: string}>();
    
    const [timeFrame, setTimeFrame] = useState<DateRangeValue>(
        getLocalStorageItem('analyticsTimeFrame', {
          from: startOfToday.startOf("month").startOf('day').toDate(),
          to: endOfToday.toDate(),
          label: 'This month'
        })
    );
    
    // Track if we should stop retrying after errors
    const [shouldFetchData, setShouldFetchData] = useState(true);

    const decodedCategory = decodeURIComponent(params.category);
    const { data: individualCategoryProfitBreakDownData, isLoading: isIndividualProfitabilityLoading, error: profitabilityError } = useCache<ProfitabilityByCategoryBreakdown>(
        computeCacheKey(timeFrame, `/seller-analytics/profit-by-category/${decodedCategory}`), 
        shouldFetchData ? () => getCategoryProfitabilityBreakDownByCategory({ category: decodedCategory, interval: timeFrame }) : null
    );
    
    // Stop retrying when errors occur and show error modal
    useEffect(() => {
        if (profitabilityError && shouldFetchData) {
            console.error('Category profitability data fetch failed:', profitabilityError);
            handleErrorResponseGenerically([{
                message: 'Unable to retrieve category analytics data. Please try again later.',
            }]);
            setShouldFetchData(false);
        }
    }, [profitabilityError, shouldFetchData, handleErrorResponseGenerically]);
    
    // Reset retry flags when timeFrame or category changes
    useEffect(() => {
        setShouldFetchData(true);
    }, [timeFrame, decodedCategory]);

    const { metricsData, salesRefundData, marginData, shippingCostData, roiData, buyCostData } = useMemo(() => {
        if (!individualCategoryProfitBreakDownData?.salesBreakdownByDate) {
            return { metricsData: null, salesRefundData: null, marginData: null, shippingCostData: null, roiData: null, buyCostData: null };
        }

        const { salesBreakdownByDate, totalRevenue, totalFees, totalProfit, totalSales, totalRefunds, marginPercent, roiPercent, buyCosts } = individualCategoryProfitBreakDownData;

        const [metricsChartData, salesRefundChartData, marginChartData, shippingCostChartData] = salesBreakdownByDate.reduce((acc, item, index) => {
            acc[0].push({
                day: index + 1,
                revenue: item.revenue,
                fees: item.fees,
                profit: item.profit,
                cogs: item.cogs
            });

            acc[1].push({
                day: index + 1,
                sales: item.sales,
                refunds: item.refunds
            });

            acc[2].push({
                date: index + 1,
                value: item.margin
            });

            acc[3].push({
                data: index + 1,
                value: item.cogs
            })

            return acc;
        }, [[], [], [], []]);

        return {
            metricsData: {
                chartData: metricsChartData,
                totals: {
                    revenue: { value: totalRevenue },
                    fees: { value: totalFees },
                    profit: { value: totalProfit },
                    cogs: { value: buyCosts }
                }
            },
            salesRefundData: {
                chartData: salesRefundChartData,
                totals: [
                    {
                        key: 'sales',
                        label: 'Sales',
                        color: 'var(--color-primary)',
                        total: totalSales,
                    },
                    {
                        key: 'refunds',
                        label: 'Refunds',
                        color: 'var(--static-background-inverse-standard)',
                        total: totalRefunds,
                    }
                ]
            },
            marginData: {
                chartData: marginChartData,
                total: marginPercent
            },
            shippingCostData: {
                chartData: shippingCostChartData,
                total: 0
            },
            roiData: {
                chartData: [],
                total: roiPercent
            },
            buyCostData: {
                total: buyCosts
            }
        };
    }, [individualCategoryProfitBreakDownData]);

    return (
        <PageContainer>
            <div className={styles.pageContainer}>
                <div className={styles.topRowContainer}>
                    <Link className={styles.backButton} href={"/profit-by-category?analytics=true"}>
                        <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none">
                            <path
                                d="M13.3 17.2998L8.70005 12.6998C8.60005 12.5998 8.52938 12.4915 8.48805 12.3748C8.44605 12.2581 8.42505 12.1331 8.42505 11.9998C8.42505 11.8665 8.44605 11.7415 8.48805 11.6248C8.52938 11.5081 8.60005 11.3998 8.70005 11.2998L13.3 6.6998C13.4834 6.51647 13.7167 6.4248 14 6.4248C14.2834 6.4248 14.5167 6.51647 14.7 6.6998C14.8834 6.88314 14.975 7.11647 14.975 7.3998C14.975 7.68314 14.8834 7.91647 14.7 8.0998L10.8 11.9998L14.7 15.8998C14.8834 16.0831 14.975 16.3165 14.975 16.5998C14.975 16.8831 14.8834 17.1165 14.7 17.2998C14.5167 17.4831 14.2834 17.5748 14 17.5748C13.7167 17.5748 13.4834 17.4831 13.3 17.2998Z"
                                fill="var(--static-text-standard)"/>
                        </svg>
                        <span className={styles.title}>
                            {isIndividualProfitabilityLoading ? <Skeleton width={"100%"} height={32} /> : individualCategoryProfitBreakDownData?.category}
                        </span>
                    </Link>
                    <DateRangeButtons
                        timeFrame={timeFrame}
                        setTimeFrame={setTimeFrame}
                    />
                </div>
                <div className={styles.container}>
                    <div className={styles.salesLineChart}>
                        <MetricLineChart
                            data={metricsData?.chartData}
                            metrics={metrics}
                            totals={metricsData?.totals}
                            valueFormatter={(value) => value >= 1000 ? `$${Math.round(value / 1000)}K` : `$${Math.round(value)}`}
                            xAxisFormatter={(day) => day.toString()}
                            isLoading={isIndividualProfitabilityLoading || !metricsData}
                        />
                    </div>
                    <div className={styles.salesBarChart}>
                        <MetricStackedBarChart
                            data={salesRefundData?.chartData}
                            metrics={salesRefundData?.totals}
                            height={'300'}
                            isLoading={isIndividualProfitabilityLoading || !salesRefundData}
                        />
                    </div>
                    <div className={styles.smallerChartsGrid}>
                        <div className={styles.smallChart}>
                            <RadialPercentageChart 
                                percentage={individualCategoryProfitBreakDownData?.roiPercent} 
                                title={"ROI %"} 
                                color={"var(--primary-enabled)"}
                                isLoading={isIndividualProfitabilityLoading || !individualCategoryProfitBreakDownData}
                            />
                        </div>
                        <div className={styles.smallChart}>
                            <AreaChart 
                                data={marginData?.chartData} 
                                title={"Margin"} 
                                currentValue={marginData?.total} 
                                valueFormatter={(value) => {
                                    return `${value}%`
                                }}
                            />
                        </div>
                        <div className={styles.smallChart}>
                            <BuyCostTile
                                buyCost={buyCostData?.total} 
                                allowEdit={false}
                                isLoading={isIndividualProfitabilityLoading || !buyCostData}
                            />
                        </div>
                        <div className={styles.smallChart}>
                            <RadialPercentageChart 
                                percentage={individualCategoryProfitBreakDownData?.revenuePercent} 
                                title={"% of total Revenue"} 
                                color={"#5823FF"}
                                isLoading={isIndividualProfitabilityLoading || !individualCategoryProfitBreakDownData}
                            />
                        </div>
                    </div>
                </div>
                <div>
                    <ProductsSoldTable />
                </div>
            </div>
        </PageContainer>
    )
}

export default ProfitByCategoryPage;